<script lang="ts">
  import Kbd from './Kbd.svelte';
  // Search field with a magnifier, a clear button and an optional shortcut.
  //   <SearchInput bind:value={query} shortcut="/" placeholder={t('entries.search')} />
  // Escape clears the text (a second Escape blurs). With a single-key
  // `shortcut` ("/") pressing that key anywhere outside a text field focuses
  // the search; the hint is shown while the field is empty and not focused.
  import { Search, X } from '@lucide/svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    value?: string;
    placeholder?: string;
    /** Shortcut hint, e.g. "/"; a single character is also bound as key. */
    shortcut?: string;
    size?: 'sm' | 'md';
    ariaLabel?: string;
    id?: string;
    disabled?: boolean;
    autofocus?: boolean;
    el?: HTMLInputElement | null;
    oninput?: (value: string) => void;
    onescape?: () => void;
    onsubmit?: (value: string) => void;
    class?: string;
  }
  let {
    value = $bindable(''),
    placeholder,
    shortcut,
    size = 'md',
    ariaLabel,
    id,
    disabled = false,
    autofocus = false,
    el = $bindable(null),
    oninput,
    onescape,
    onsubmit,
    class: cls = ''
  }: Props = $props();

  let focused = $state(false);

  function set(next: string) {
    value = next;
    oninput?.(next);
  }

  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (value) {
        event.preventDefault();
        event.stopPropagation();
        set('');
      } else {
        el?.blur();
      }
      onescape?.();
    } else if (event.key === 'Enter') {
      onsubmit?.(value);
    }
  }

  function isTyping(target: EventTarget | null): boolean {
    const node = target as HTMLElement | null;
    if (!node) return false;
    return node.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(node.tagName);
  }

  $effect(() => {
    if (!shortcut || shortcut.length !== 1) return;
    const key = shortcut;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== key || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target) || disabled) return;
      event.preventDefault();
      el?.focus();
      el?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  $effect(() => {
    if (autofocus && el) el.focus();
  });
</script>

<div class="search {size} {cls}" class:disabled>
  <Search class="glass" aria-hidden="true" />
  <input
    bind:this={el}
    {id}
    type="search"
    class="inp"
    autocomplete="off"
    spellcheck="false"
    placeholder={placeholder ?? t('ui.search_placeholder')}
    aria-label={ariaLabel || placeholder || t('ui.search')}
    aria-keyshortcuts={shortcut}
    {disabled}
    {value}
    oninput={(e) => set(e.currentTarget.value)}
    onkeydown={keydown}
    onfocus={() => (focused = true)}
    onblur={() => (focused = false)}
  />
  {#if value}
    <button type="button" class="clear" aria-label={t('ui.clear')} onclick={() => { set(''); el?.focus(); }}><X /></button>
  {:else if shortcut && !focused}
    <Kbd size="md" title={t('ui.search_shortcut', { key: shortcut })}>{shortcut}</Kbd>
  {/if}
</div>

<style>
  .search {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    height: var(--control-h);
    padding: 0 var(--space-2) 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-pill);
    background: var(--surface);
    color: var(--text-3);
    min-width: 0;
    transition: border-color var(--dur), box-shadow var(--dur);
  }
  .sm { height: var(--control-h-sm); padding-left: var(--space-2_5); }
  .search:hover { border-color: var(--border-strong); }
  .search:focus-within { border-color: var(--brand); box-shadow: var(--ring); }
  .disabled { opacity: 0.6; background: var(--surface-2); }
  .search :global(.glass) { width: var(--icon-md); height: var(--icon-md); }
  .sm :global(.glass) { width: var(--icon-sm); height: var(--icon-sm); }
  .inp {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: var(--text);
    font-size: var(--text-base);
    outline: none;
  }
  .sm .inp { font-size: var(--text-sm); }
  .inp::placeholder { color: var(--text-3); }
  .inp::-webkit-search-cancel-button { display: none; }
  .clear {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    border-radius: var(--radius-full);
    background: var(--surface-3);
    color: var(--text-2);
  }
  .clear :global(svg) { width: var(--icon-xs); height: var(--icon-xs); }
  .clear:hover { color: var(--text); }
  .clear:focus-visible { outline: none; box-shadow: var(--ring); }
</style>
